import * as AbWorkflow from '../util/workflow'
import '../util/ab-datasource.js'
import {
  isNeutralValue,
  convertValue,
  convertFieldName,
  getTotalNumberOfRecords
} from '../util/util-datasource'
import camelCase from 'lodash/camelCase'
import isFunction from 'lodash/isFunction'
import isObject from 'lodash/isObject'

/**
 * Retrieves data from the Web Central server using a file data source
 * @param view The name of the axvw view file that contains the data source definition
 * @param dataSourceName The name of the data source
 * @param restriction The Ab.data.Restriction to be applied to the data source
 * @param parameters Data source parameters
 * @returns {Promise<any | never>}
 */
export const getDataFromDataSource = async (
  view,
  dataSourceName,
  restriction = null,
  parameters = []
) => {
  // we need a data source instance to query records and to format record values

  const {code, data, detailedMessage} = await AbWorkflow.callMethod(
    'AbCommonResources-DataSourceService-loadDataSourceFromFile',
    [view, dataSourceName]
  )

  const {primaryKeyFields} = data

  if (code === 'executed') {
    let dataSource = Ab.data.createDataSourceForFields(data)

    parameters.forEach(({name, value}) => dataSource.addParameter(name, value))

    try {
      const records = await dataSource.getRecordsAsync(restriction)

      let convertedData = records.map(record => {
        let values = record.getValues()
        return convertDataTypes({values, dataSource, primaryKeyFields})
      })

      const fieldDefs = convertFieldDefs(dataSource)

      return {fieldDefs, data: convertedData}
    } catch (e) {
      throw Error(e)
    }
  } else {
    throw Error(detailedMessage)
  }
}

export const getPagedDataFromDataSource = (
  view,
  dataSourceName,
  restriction = null,
  parameters = [],
  page = 1,
  pageSize = 100
) => {
  // we need a data source instance to query records and to format record values
  let dataSource
  return AbWorkflow.callMethod(
    'AbCommonResources-DataSourceService-loadDataSourceFromFile',
    [view, dataSourceName]
  )
    .then(result => {
      if (result.code === 'executed') {
        dataSource = Ab.data.createDataSourceForFields(result.data)

        parameters.forEach(parameter =>
          dataSource.addParameter(parameter.name, parameter.value)
        )

        let pageStart = (page - 1) * pageSize + 1

        return dataSource.getDataSetAsync(restriction, {
          showTotals: true,
          sortValues: [],
          filterValues: [],
          pageStart,
          pageSize
        })
      } else {
        return Promise.reject(result.detailedMessage)
      }
    })
    .then(result => {
      const {records, totals} = result
      let total = getTotalNumberOfRecords(dataSource, totals)

      let data = records.map(record => {
        let values = record.getValues()
        return convertDataTypes({values, dataSource})
      })

      const fieldDefs = convertFieldDefs(dataSource)

      return Promise.resolve({data, fieldDefs, total})
    })
}

const renameFormattedValues = formattedValues => {
  let renamedValues = {}
  Object.keys(formattedValues).forEach(
    key => (renamedValues[`${key}_format`] = formattedValues[key])
  )
  return renamedValues
}

const isPrimaryKeyLocalized = ({primaryKeyFields = [], values}) =>
  primaryKeyFields.some(field => !!values[`${field}.nl`])

const convertDataTypes = ({values, dataSource, primaryKeyFields = []}) => {
  const {tableNames, fieldDefs} = dataSource

  const isPrimaryKeyFieldLocalized = isPrimaryKeyLocalized({
    primaryKeyFields,
    values
  })

  if (isPrimaryKeyFieldLocalized) {
    primaryKeyFields.forEach(field => {
      values[field] = values[`${field}.nl`] || values[field]
    })
  }

  let formattedValues = renameFormattedValues(
    dataSource.formatValues(values, true, true)
  )
  let fieldValues = {...values, ...formattedValues}
  return Object.keys(fieldValues).reduce((acc, curVal) => {
    if (!isNeutralValue(curVal)) {
      let tableDefFieldName =
        curVal.substring(curVal.length - 7) === '_format'
          ? curVal.substring(0, curVal.length - 7)
          : curVal
      let fieldDef = fieldDefs.get(tableDefFieldName)
      if (fieldDef) {
        let fieldName = camelCase(convertFieldName(curVal, tableNames))
        acc[fieldName] = convertValue(fieldValues[curVal], fieldDef.type)
      }
    }

    return acc
  }, {})
}

const convertFieldDefs = dataSource =>
  dataSource.fieldDefs.keys.reduce((acc, curVal) => {
    let fieldName = camelCase(convertFieldName(curVal, dataSource.tableNames))
    let fieldDef = dataSource.fieldDefs.get(curVal)
    let fieldDefProps = {}
    Object.keys(fieldDef).forEach(key => {
      // Exclude the config and constructor properties
      let isFunc = isFunction(fieldDef[key])
      let isConfigObject = isObject(fieldDef[key]) && key === 'config'

      if (!isFunc && !isConfigObject && fieldDef.hasOwnProperty(key)) {
        fieldDefProps[key] = fieldDef[key]
      }
    })
    acc[fieldName] = fieldDefProps
    return acc
  }, {})

export const saveDataRecord = (view, dataSource, data, isNew = true) => {
  return AbWorkflow.callMethod(
    'AbCommonResources-DataSourceService-loadDataSourceFromFile',
    [view, dataSource]
  ).then(result => {
    if (result.code === 'executed') {
      dataSource = Ab.data.createDataSourceForFields(result.data)
      let record = new Ab.data.Record()
      record.isNew = isNew
      Object.keys(data).forEach(key => record.setValue(key, data[key]))
      // TODO: Make dataSource.saveRecord async
      let newRecord = dataSource.saveRecord(record)
      return Promise.resolve(newRecord)
    } else {
      return Promise.reject(result.detailedMessage)
    }
  })
}

export const updateDataRecord = (view, dataSource, record) => {
  return AbWorkflow.callMethod(
    'AbCommonResources-DataSourceService-loadDataSourceFromFile',
    [view, dataSource]
  ).then(result => {
    if (result.code === 'executed') {
      dataSource = Ab.data.createDataSourceForFields(result.data)
      dataSource.saveRecord(record)
    } else {
      return Promise.reject(result.detailedMessage)
    }
  })
}

export const getDataRecords = async ({
  viewName,
  dataSourceId,
  restriction = {},
  recordLimit = 0
}) => {
  const result = await AbWorkflow.call('AbCommonResources-getDataRecords', {
    viewName,
    dataSourceId,
    restriction,
    recordLimit
  })

  const {data} = result

  return data.records
}
